import { useState, useCallback, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { HardDrive, Loader2, Search, FileText, Image as ImageIcon, Link2, Folder, ChevronRight, ArrowLeft } from "lucide-react";

const FOLDER_MIME = "application/vnd.google-apps.folder";

export default function DriveImport({ targetType, targetId, onImported, label = "Drive", variant = "link" }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [files, setFiles] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [importingId, setImportingId] = useState(null);
  const [stack, setStack] = useState([]); // [{id, name}] breadcrumb of folders
  const [searching, setSearching] = useState(false);
  const [source, setSource] = useState("my");
  const msgRef = useRef(null);

  const currentFolderId = stack.length ? stack[stack.length - 1].id : null;

  const checkStatus = useCallback(async () => {
    try { const { data } = await api.get("/drive/status"); setStatus(data); return data; }
    catch { const s = { configured: false, connected: false }; setStatus(s); return s; }
  }, []);

  const fetchList = useCallback(async (params) => {
    setLoading(true);
    try { const { data } = await api.get("/drive/files", { params }); setFiles(data.files || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setFiles([]); }
    finally { setLoading(false); }
  }, []);

  const loadFolder = useCallback((folderId, src) => {
    setSearching(false);
    fetchList(folderId ? { folder_id: folderId } : { source: src || "my" });
  }, [fetchList]);

  const switchSource = (src) => {
    setSource(src);
    setStack([]);
    setQ("");
    loadFolder(null, src);
  };

  const runSearch = () => {
    if (q.trim()) { setSearching(true); fetchList({ q: q.trim() }); }
    else loadFolder(currentFolderId, source);
  };

  const enterFolder = (f) => {
    const base = searching ? [] : stack;
    const next = [...base, { id: f.id, name: f.name }];
    setStack(next);
    setQ("");
    loadFolder(f.id);
  };

  const gotoCrumb = (index) => {
    if (index < 0) { setStack([]); setQ(""); loadFolder(null, source); return; }
    const next = stack.slice(0, index + 1);
    setStack(next);
    setQ("");
    loadFolder(next[next.length - 1].id);
  };

  const openModal = async () => {
    setOpen(true);
    setStatus(null);
    setStack([]);
    setQ("");
    setSearching(false);
    setSource("my");
    const s = await checkStatus();
    if (s.configured && s.connected) loadFolder(null, "my");
  };

  const connect = async () => {
    try {
      const { data } = await api.get("/drive/connect");
      window.open(data.authorization_url, "drive-oauth", "width=520,height=640");
      if (msgRef.current) window.removeEventListener("message", msgRef.current);
      const onMsg = async (ev) => {
        if (ev.data === "drive-connected") {
          window.removeEventListener("message", onMsg);
          const s = await checkStatus();
          if (s.connected) loadFolder(null, "my");
        } else if (ev.data === "drive-error") {
          window.removeEventListener("message", onMsg);
          toast.error("Google Drive connection failed");
        }
      };
      msgRef.current = onMsg;
      window.addEventListener("message", onMsg);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const disconnect = async () => {
    try { await api.post("/drive/disconnect"); setFiles([]); setStack([]); checkStatus(); } catch (e) {}
  };

  const doImport = async (f) => {
    setImportingId(f.id);
    try {
      const { data } = await api.post("/drive/import", { file_id: f.id, target_type: targetType, target_id: targetId });
      toast.success(`Imported ${f.name}`);
      setOpen(false);
      onImported?.(data.parent);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setImportingId(null); }
  };

  const btnClass = variant === "outline"
    ? "flex items-center gap-1 border border-zinc-200 px-2 py-1 text-xs text-zinc-600 hover:border-[#b32233]"
    : "flex items-center gap-1 text-xs font-medium text-[#b32233] hover:underline";

  const folders = files.filter((f) => f.mimeType === FOLDER_MIME);
  const docs = files.filter((f) => f.mimeType !== FOLDER_MIME);

  return (
    <>
      <button type="button" onClick={openModal} data-testid={`drive-import-${targetType}`} className={btnClass}>
        <HardDrive className="h-3 w-3" /> {label}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Import from Google Drive</DialogTitle>
            <DialogDescription>Browse folders or search, then pick a file to import a copy.</DialogDescription>
          </DialogHeader>
          {status === null ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#b32233]" /></div>
          ) : !status.configured ? (
            <p className="py-8 text-center text-sm text-zinc-500" data-testid="drive-not-configured">Google Drive isn't configured on the server yet.</p>
          ) : !status.connected ? (
            <div className="flex flex-col items-center gap-3 py-8">
              <p className="text-sm text-zinc-600">Connect your Google Drive to browse and import files.</p>
              <button onClick={connect} data-testid="drive-connect-button" className="flex items-center gap-2 bg-[#b32233] px-4 py-2 text-sm font-medium text-white hover:bg-[#8f1a28]"><Link2 className="h-4 w-4" /> Connect Google Drive</button>
            </div>
          ) : (
            <div>
              <form onSubmit={(e) => { e.preventDefault(); runSearch(); }} className="mb-3 flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="drive-search" placeholder="Search all of Drive…" className="w-full border border-zinc-200 py-2 pl-8 pr-2 text-sm outline-none focus:border-[#b32233]" />
                </div>
                <button type="submit" className="bg-zinc-900 px-3 text-sm font-medium text-white hover:bg-zinc-700">Search</button>
              </form>

              {!searching && (
                <div className="mb-2 flex gap-1">
                  <button type="button" onClick={() => switchSource("my")} data-testid="drive-source-my" className={`border px-2 py-1 text-xs font-medium transition-colors ${source === "my" ? "border-[#b32233] bg-[#b32233] text-white" : "border-zinc-200 text-zinc-600 hover:border-zinc-300"}`}>My Drive</button>
                  <button type="button" onClick={() => switchSource("shared")} data-testid="drive-source-shared" className={`border px-2 py-1 text-xs font-medium transition-colors ${source === "shared" ? "border-[#b32233] bg-[#b32233] text-white" : "border-zinc-200 text-zinc-600 hover:border-zinc-300"}`}>Shared with me</button>
                </div>
              )}

              {searching ? (
                <button onClick={() => { setQ(""); loadFolder(currentFolderId, source); }} data-testid="drive-back-to-folders" className="mb-2 flex items-center gap-1 text-xs font-medium text-[#b32233] hover:underline">
                  <ArrowLeft className="h-3 w-3" /> Back to folders
                </button>
              ) : (
                <div className="mb-2 flex flex-wrap items-center gap-1 text-xs text-zinc-500" data-testid="drive-breadcrumb">
                  <button onClick={() => gotoCrumb(-1)} className="font-medium hover:text-[#b32233]">{source === "shared" ? "Shared with me" : "My Drive"}</button>
                  {stack.map((c, i) => (
                    <span key={c.id} className="flex items-center gap-1">
                      <ChevronRight className="h-3 w-3 text-zinc-300" />
                      <button onClick={() => gotoCrumb(i)} className="max-w-[140px] truncate hover:text-[#b32233]">{c.name}</button>
                    </span>
                  ))}
                </div>
              )}

              {loading ? (
                <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#b32233]" /></div>
              ) : files.length === 0 ? (
                <p className="py-8 text-center text-sm text-zinc-400">{searching ? "No files found." : "This folder is empty."}</p>
              ) : (
                <div className="max-h-80 space-y-1 overflow-auto">
                  {folders.map((f) => (
                    <button key={f.id} onClick={() => enterFolder(f)} data-testid={`drive-folder-${f.id}`} className="flex w-full items-center gap-2 border border-zinc-100 px-2 py-2 text-left text-sm transition-colors hover:border-[#b32233] hover:bg-zinc-50">
                      <Folder className="h-4 w-4 text-[#b32233]" />
                      <span className="min-w-0 flex-1 truncate font-medium text-zinc-800">{f.name}</span>
                      <ChevronRight className="h-4 w-4 text-zinc-300" />
                    </button>
                  ))}
                  {docs.map((f) => {
                    const isImg = (f.mimeType || "").startsWith("image/");
                    return (
                      <button key={f.id} onClick={() => doImport(f)} disabled={!!importingId} data-testid={`drive-file-${f.id}`} className="flex w-full items-center gap-2 border border-zinc-100 px-2 py-2 text-left text-sm transition-colors hover:border-[#b32233] hover:bg-zinc-50 disabled:opacity-50">
                        {f.iconLink ? <img src={f.iconLink} alt="" className="h-4 w-4" /> : isImg ? <ImageIcon className="h-4 w-4 text-zinc-400" /> : <FileText className="h-4 w-4 text-zinc-400" />}
                        <span className="min-w-0 flex-1 truncate text-zinc-800">{f.name}</span>
                        {importingId === f.id && <Loader2 className="h-4 w-4 animate-spin text-[#b32233]" />}
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="mt-3 flex items-center justify-between text-xs text-zinc-400">
                <span className="truncate">{status.email}</span>
                <button onClick={disconnect} data-testid="drive-disconnect-button" className="hover:text-[#b32233]">Disconnect</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
